/* eslint-disable no-underscore-dangle */

import * as React from 'react';

import * as digitalData from '@ao-internal/digital-data';

import NoImage from '../../assets/images/no-image.png';
import { TaggingEventNames } from '../../globals';
import useImageLoader from '../../hooks/useImageLoader';
import { ChatMessageTypes, ChatMessageTypesClassMap } from '../../store';
import ProductSummary, { ProductSummaryModel } from '../ProductSummary';

export type RecommendedProductMessageContent = ProductSummaryModel;

interface RecommendedProductsMessageProps {
	products: RecommendedProductMessageContent[];
}

export enum RecommendedProductTaggingEventTypes {
	View,
	Click,
}

export const dataTestId = `${
	ChatMessageTypesClassMap[ChatMessageTypes.RecommendedProducts]
}-message`;

export function createRecommendedProductAdobeTaggingStrings(
	eventType: RecommendedProductTaggingEventTypes,
	productData: RecommendedProductMessageContent,
	position: number
): string {
	const eventName =
		eventType === RecommendedProductTaggingEventTypes.View ? 'event156=1;' : '';

	const { review, productCode, title } = productData;
	const savingsAmount = productData.savingsAmount
		? productData.savingsAmount
		: 0;
	const rating =
		review && review.totalReviewCount > 0 ? review.averageRating : 0;

	// The leading semicolon (;) and trailing comma (,) are deliberate as even a single product requires leading and trailing chars.
	// Read more here: https://www.blastanalytics.com/blog/mastering-adobe-analytics-s-products
	return `;${productCode};;;${eventName}eVar155=chatbot recommended product_${title}_${position}_${savingsAmount}_${rating}_standard,`;
}

export const RecommendedProductsMessage: React.FC<RecommendedProductsMessageProps> =
	({ products }) => {
		const productImages = useImageLoader(
			products.map((product) => {
				return { src: product.imageUrl, placeholder: NoImage };
			})
		);

		function handleClick(evt: React.MouseEvent, productIndex: number) {
			const productData = products[productIndex];

			/* eslint-disable */
			if (window._satellite && window._satellite.setVar) {
				window._satellite.setVar(
					TaggingEventNames.recommendedProductsClicked,
					createRecommendedProductAdobeTaggingStrings(
						RecommendedProductTaggingEventTypes.Click,
						productData,
						productIndex + 1
					)
				);
			}
			/* eslint-enable */

			digitalData.sendBeacon(TaggingEventNames.recommendedProductsClicked);
		}

		React.useEffect(() => {
			/* eslint-disable */
			if (window._satellite && window._satellite.setVar) {
				window._satellite.setVar(
					TaggingEventNames.recommendedProductsViewed,
					products
						.map((productData, productIndex) =>
							createRecommendedProductAdobeTaggingStrings(
								RecommendedProductTaggingEventTypes.View,
								productData,
								productIndex + 1
							)
						)
						.join('')
				);
			}
			/* eslint-enable */

			digitalData.sendBeacon(TaggingEventNames.recommendedProductsViewed);
		}, []); // eslint-disable-line react-hooks/exhaustive-deps

		return (
			<div
				data-testid={dataTestId}
				data-tag-area="recommended products message"
				className="mx-2"
			>
				{products
					.filter((product) => product.isForSale !== false)
					.map((product: RecommendedProductMessageContent, index: number) => {
						const position = index + 1;
						const productImage = productImages[index];

						return (
							<div
								key={`recommended-product-${product.productCode}`}
								data-testid="recommended-product"
								className="border-gray-40 text-caption font-regular mb-2 break-words bg-white border border-solid rounded-md"
							>
								<ProductSummary
									className="c-chat-recommended-product rounded-md"
									taggingPrefix="recommended-product"
									as="a"
									product={{ ...product, imageUrl: productImage.src }}
									href={product.url}
									onClick={(evt: React.MouseEvent) => handleClick(evt, index)}
									data-tag-name="product link"
									data-tag-position={position}
									data-tag-sku={product.productCode}
									data-tag-review-count={
										product.review && product.review.totalReviewCount > 0
											? product.review.averageRating
											: 0
									}
									data-tag-rating={
										product.review && product.review.totalReviewCount > 0
											? product.review.totalReviewCount
											: 0
									}
									data-tag-price={product.formattedPrice}
									data-tag-saving={
										product.savingsAmount ? product.formattedSavingsAmount : 0
									}
								/>
							</div>
						);
					})}
			</div>
		);
	};

export default RecommendedProductsMessage;
